import { useNotifySocialWorker } from "apollo/hooks/mutations";
import { TRACK_EVENTS } from "core/consts";
import { getName } from "core/model/accounts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Account, Auction, Patient } from "core/types";
import { ChangeDialog } from "dsl/molecules/ReceiverAssigneeChange";
import { useCallback, useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

export function differentAssignee(
  newPatient: Patient | undefined,
  oldPatient: Patient | undefined,
  account: Account | undefined,
  initialAuction?: Auction,
): [boolean, number | null | undefined] {
  const newAssignee = newPatient?.social_worker;
  let oldAssignee;
  if (initialAuction?.assessment_completed) {
    oldAssignee = initialAuction?.patient.assignee;
  } else {
    oldAssignee = oldPatient?.social_worker;
  }

  const assigneeChanged =
    oldAssignee &&
    newAssignee &&
    oldAssignee?.id !== newAssignee?.id &&
    // Don't notify yourself
    newAssignee.id !== account?.id;

  if (assigneeChanged) return [true, newAssignee?.id];

  return [false, null];
}

export default function ChangeSocialWorker({
  assigned,
  nextNotification,
  patientId,
}: {
  assigned: Account | null;
  nextNotification?: () => void;
  patientId: number;
}) {
  const [notifySocialWorker, queryProgress] = useNotifySocialWorker({
    patientId,
  });
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const { trackEvent } = useTracking();

  const skip = useCallback(() => {
    trackEvent({
      action: "skip",
      name: TRACK_EVENTS.NOTIFY_SOCIAL_WORKER,
    });

    if (nextNotification) nextNotification();
  }, [nextNotification]);

  const [text, setText] = useState("");

  const confirm = useCallback(() => {
    trackEvent({
      action: "notify",
      name: TRACK_EVENTS.NOTIFY_SOCIAL_WORKER,
    });

    notifySocialWorker({ message: text }).finally(() => {
      if (nextNotification) nextNotification();
    });
  }, [notifySocialWorker, text]);

  return (
    <ChangeDialog
      confirm={confirm}
      queryProgress={queryProgress}
      setText={setText}
      skip={skip}
      text={text}
      title={translations.patient.notifySocialWorker.title({
        assignedName: getName(assigned, getOntology, {
          withAcademicTitle: true,
          withSalutation: true,
        }),
      })}
      translations={translations}
    />
  );
}
